import Link from 'next/link';
import Image from 'next/image';
import { CategoryId, getCategoriesByIds } from '@/lib/categories';
import { getDisplayStatus, getStatusColor } from '@/lib/campaign-status';
import { getChainOption } from '@/lib/chain-config';

interface CampaignCardProps {
  campaign: {
    id: string;
    title: string;
    headline?: string;
    description: string;
    slug: string;
    orgName: string;
    locationText: string;
    categories: CategoryId[];
    chain?: string;
    coverImageUrl?: string | null;
    clueCount: number;
    claimCount: number;
    status: 'DRAFT' | 'PUBLISHED' | 'ACTIVE' | 'ENDED' | 'ARCHIVED';
    startAt?: string | Date | null;
    endAt?: string | Date | null;
    createdAt: Date;
    tenant: {
      name: string;
      slug: string;
      logoUrl?: string | null;
    };
  };
  /** Tenant primary color for whitelabel theming of the Join button. */
  primaryColor?: string;
}

export function CampaignCard({ campaign, primaryColor }: CampaignCardProps) {
  const categories = getCategoriesByIds(campaign.categories);
  const chainOption = campaign.chain ? getChainOption(campaign.chain) : undefined;
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col h-full">
      {/* Cover Image */}
      <div className="relative h-48 bg-gray-200">
        {campaign.coverImageUrl ? (
          <Image
            src={campaign.coverImageUrl}
            alt={campaign.title}
            fill
            className="object-cover"
            unoptimized
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
            <div className="text-white text-4xl">🏃‍♂️</div>
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <span className={`
            inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
            ${getStatusColor(getDisplayStatus(campaign))}
          `}>
            {getDisplayStatus(campaign)}
          </span>
        </div>

        {/* Chain Logo */}
        {chainOption && (
          <div className="absolute bottom-2 right-2 w-7 h-7 rounded-full overflow-hidden bg-white shadow-sm border border-gray-200" title={chainOption.label}>
            <Image
              src={chainOption.logo}
              alt={chainOption.label}
              width={28}
              height={28}
              className="w-full h-full object-contain"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        {/* Categories */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {categories.slice(0, 2).map((category) => (
              <Link
                key={category.id}
                href={`/browse/category/${category.id}`}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                <span className="mr-1">{category.icon}</span>
                {category.name}
              </Link>
            ))}
            {categories.length > 2 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                +{categories.length - 2} more
              </span>
            )}
          </div>
        )}

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
          <Link
            href={`/${campaign.tenant.slug}/campaigns/${campaign.slug}`}
            className="hover:text-blue-600 transition-colors"
          >
            {campaign.title}
          </Link>
        </h3>

        {/* Location */}
        {campaign.locationText && (
          <p className="text-sm text-gray-500 mb-3">{campaign.locationText}</p>
        )}

        {/* Headline */}
        {campaign.headline && (
          <p className="text-gray-600 text-sm mb-4 whitespace-pre-line">
            {campaign.headline}
          </p>
        )}

        {/* Organization */}
        <div className="mt-auto flex items-center text-sm text-gray-500 mb-4">
          {campaign.tenant.logoUrl ? (
            <Image
              src={campaign.tenant.logoUrl}
              alt={campaign.orgName}
              width={20}
              height={20}
              className="rounded-full mr-2"
            />
          ) : (
            <div className="w-5 h-5 bg-gray-300 rounded-full mr-2 flex items-center justify-center">
              <span className="text-xs">🏢</span>
            </div>
          )}
          <span className="font-medium">{campaign.orgName}</span>
        </div>

        {/* Stats */}
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {campaign.clueCount} {campaign.clueCount === 1 ? 'location' : 'locations'}
            </span>
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              {campaign.claimCount} {campaign.claimCount === 1 ? 'participant' : 'participants'}
            </span>
          </div>
        </div>

        {/* Join Button */}
        <Link
          href={`/${campaign.tenant.slug}/campaigns/${campaign.slug}`}
          className={`w-full inline-flex items-center justify-center px-4 py-2.5 text-white font-medium rounded-lg transition-colors ${primaryColor ? 'hover:opacity-90' : 'bg-blue-600 hover:bg-blue-700'}`}
          style={primaryColor ? { backgroundColor: primaryColor } : undefined}
        >
          Join Experience
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
